import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { createProject, updateProject, deleteProject } from '../../../api/projects';
import { provisionAutopilot } from '../../../api/autopilot';
import { canDeleteProject } from '../../../middleware/betaPermissions';
import { useToast } from '../../common/Toast';
import PreviewAccountsSection from './PreviewAccountsSection';
import './ProjectCreateModal.scss';

const COLOR_PRESETS = [
  '#667eea', '#5c6bc0', '#42a5f5', '#26c6da',
  '#66bb6a', '#9ccc65', '#ffca28', '#ffa726',
  '#ef5350', '#ec407a', '#ab47bc', '#8d6e63',
];

/**
 * ProjectCreateModal — 建立 / 編輯專案
 * @param {object|null} project — 傳入則為編輯模式
 */
const ProjectCreateModal = ({ isOpen, onClose, onSuccess, project = null }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { refreshProjects } = useBetaData();
  const toast = useToast();
  const isEdit = !!project;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [color, setColor] = useState('#667eea');
  const [milestoneStart, setMilestoneStart] = useState('');
  const [milestoneEnd, setMilestoneEnd] = useState('');
  const [autopilotConfigText, setAutopilotConfigText] = useState('');
  const [autopilotConfigError, setAutopilotConfigError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (project) {
        setName(project.name || '');
        setDescription(project.description || '');
        setExternalUrl(project.external_url || '');
        setColor(project.color || '#667eea');
        setMilestoneStart(project.milestone_start_date || '');
        setMilestoneEnd(project.milestone_end_date || '');
        setAutopilotConfigText(project.autopilot_config ? JSON.stringify(project.autopilot_config, null, 2) : '');
      } else {
        setName('');
        setDescription('');
        setExternalUrl('');
        setColor('#667eea');
        setMilestoneStart('');
        setMilestoneEnd('');
        setAutopilotConfigText('');
      }
      setDateError('');
      setAutopilotConfigError('');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, project]);

  const handleAutopilotConfigChange = (val) => {
    setAutopilotConfigText(val);
    if (!val.trim()) { setAutopilotConfigError(''); return; }
    try { JSON.parse(val); setAutopilotConfigError(''); } catch { setAutopilotConfigError('不是合法的 JSON'); }
  };

  if (!isOpen) return null;

  const handleEndDateChange = (val) => {
    setMilestoneEnd(val);
    if (milestoneStart && val && val < milestoneStart) {
      setDateError('結束日期不可早於開始日期');
    } else {
      setDateError('');
    }
  };

  const handleStartDateChange = (val) => {
    setMilestoneStart(val);
    if (milestoneEnd && val && milestoneEnd < val) {
      setDateError('結束日期不可早於開始日期');
    } else {
      setDateError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || dateError || autopilotConfigError) return;

    setSubmitting(true);

    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      external_url: externalUrl.trim() || null,
      color,
      milestone_start_date: milestoneStart || null,
      milestone_end_date: milestoneEnd || null,
      autopilot_config: autopilotConfigText.trim() ? JSON.parse(autopilotConfigText) : null,
    };

    let result;
    if (isEdit) {
      result = await updateProject(project.id, payload);
    } else {
      result = await createProject({
        ...payload,
        company_id: viewingCompanyId || profile.company_id,
        owner_id: profile.id,
      });
    }

    setSubmitting(false);

    if (!result.error) {
      // 新專案第一次設定 autopilot_config,或改了 repo 路徑/平台 → 才需要重新佈署 CI 範本 +
      // DEPLOY_PLATFORM(冪等,重複佈署也沒差,但每次存檔都打兩支 GitLab API 太慢——只在真的
      // 有變動時才打;沒變動的一般編輯(改名字/描述之類)不該多等這段)。
      const gitlabProjectPath = payload.autopilot_config?.gitlabProjectPath;
      const platformTarget = payload.autopilot_config?.deploy?.staging?.target || '';
      const platform = platformTarget.split(':')[0];
      const prevGitlabProjectPath = project?.autopilot_config?.gitlabProjectPath;
      const prevPlatformTarget = project?.autopilot_config?.deploy?.staging?.target || '';
      const configChanged = gitlabProjectPath !== prevGitlabProjectPath || platformTarget !== prevPlatformTarget;

      if (gitlabProjectPath && configChanged) {
        if (platform === 'vercel' || platform === 'firebase') {
          // 不 await——存檔視窗不用等 GitLab API 往返,失敗照樣會跳 toast,只是晚一點出現。
          provisionAutopilot(gitlabProjectPath, platform).then(({ error: provisionErr }) => {
            if (provisionErr) toast.error(`Autopilot 佈署失敗:${provisionErr}`);
          });
        } else {
          toast.error('Autopilot 設定缺 deploy.staging.target(格式如 "vercel:xxx" 或 "firebase:xxx"),沒有佈署 CI');
        }
      }
      refreshProjects();
      onSuccess?.(result.data || project);
      onClose();
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const { error } = await deleteProject(project.id);
    setSubmitting(false);

    if (error) {
      toast.error('刪除專案失敗');
    } else {
      toast.success('專案已刪除');
      refreshProjects();
      onSuccess?.(null); // null 表示已刪除
      onClose();
    }
  };

  return (
    <div className="project-create-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="project-create-modal">
        <div className="modal-header">
          <h3>{isEdit ? '編輯專案' : '建立專案'}</h3>
          <button type="button" className="close-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l10 10M14 4L4 14" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>專案名稱 *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="輸入專案名稱..."
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>說明</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="輸入專案說明（選填）..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>外部連結</label>
            <input
              type="text"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://..."
            />
            <span className="field-hint">日報中可直接開啟此連結</span>
          </div>

          <div className="form-group">
            <label>Autopilot 進階設定(JSON)</label>
            <textarea
              value={autopilotConfigText}
              onChange={(e) => handleAutopilotConfigChange(e.target.value)}
              placeholder={'{\n  "gitlabProjectPath": "lsceco/frontend/xxx",\n  "buildCmd": "...",\n  "deploy": { "staging": {...}, "prod": {...} }\n}'}
              rows={5}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <span className="field-hint">
              設定 GitLab repo 路徑 / build 指令 / 部署腳本等,才能在工作項目送「AI 修復」與部署。留空 = 停用。
              <br />
              <b>previewAuth</b>(選填 — 要截「登入後」畫面才需要):
              {' '}<code>{'{"type":"none"}'}</code> = 免登入直接截;
              {' '}<code>{'{"type":"backend-jwt","loginApi":"/api/auth/login"}'}</code> = 系統代打 loginApi 登入後再截。
              後端網址系統算不出來的獨立專案(如非 Artemis 產的),再加 <code>{'"apiBase":"https://你的後端"'}</code>。
              cookie 型 / token 型都自動處理,不用區分。帳號在下方「預覽測試帳號」登記。
            </span>
            {autopilotConfigError && <div className="date-error">{autopilotConfigError}</div>}
          </div>

          {/* 預覽測試帳號登記(編輯既有專案時才有 project.id 可掛) */}
          {isEdit && project?.id && <PreviewAccountsSection projectId={project.id} />}

          {/* Milestone 日期 —— 先隱藏,現在沒人在用(2026-08-03)。資料/驗證邏輯保留,之後要恢復直接取消註解。
          <div className="form-row">
            <div className="form-group half">
              <label>Milestone 開始</label>
              <input
                type="date"
                value={milestoneStart}
                onChange={(e) => handleStartDateChange(e.target.value)}
              />
            </div>
            <div className="form-group half">
              <label>Milestone 結束</label>
              <input
                type="date"
                value={milestoneEnd}
                onChange={(e) => handleEndDateChange(e.target.value)}
              />
            </div>
          </div>
          {dateError && <div className="date-error">{dateError}</div>}
          */}

          <div className="form-group">
            <label>顏色</label>
            <div className="color-picker">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`color-swatch ${color === c ? 'selected' : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <div className="modal-actions">
            {isEdit && canDeleteProject(profile, project) && (
              <button
                type="button"
                className="btn-delete"
                onClick={() => setShowDeleteConfirm(true)}
              >
                刪除專案
              </button>
            )}
            <div className="actions-right">
              <button type="button" className="btn-cancel" onClick={onClose}>
                取消
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={!name.trim() || !!dateError || !!autopilotConfigError || submitting}
              >
                {submitting ? (isEdit ? '儲存中...' : '建立中...') : (isEdit ? '儲存' : '建立專案')}
              </button>
            </div>
          </div>
        </form>

        {/* Delete Confirm */}
        {showDeleteConfirm && (
          <div className="delete-confirm-overlay" onClick={(e) => e.target === e.currentTarget && setShowDeleteConfirm(false)}>
            <div className="delete-confirm-dialog">
              <p className="delete-confirm-title">確定要刪除「{project?.name}」嗎？</p>
              <p className="delete-confirm-desc">此操作無法復原，專案底下的所有 Ticket 也會一併刪除。</p>
              <div className="delete-confirm-actions">
                <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>
                  取消
                </button>
                <button className="btn-danger" onClick={handleDelete} disabled={submitting}>
                  {submitting ? '刪除中...' : '確定刪除'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectCreateModal;
